import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock,
  Coffee,
  Code2,
  Timer,
  Globe,
  Calendar,
  Plus,
  Minus,
} from "lucide-react";
import "./AutomaticTimeTracking.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Testimonials from "../../components/Testimonials/Testimonials";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";
import Footer from "../../components/Footer/Footer";

const TITLE =
  "Automatic Time Tracking Software Silent Desktop Tracking | TeamGrid";
const DESCRIPTION =
  "Automatically track apps, websites and project time with a silent desktop agent. Secure, real time synced and zero manual input required";
const CANONICAL = "https://teamgrid.ai/features/automatic-time-tracking";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const AutomaticTimeTracking = () => {
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState({ 0: true });
  const toggleFaq = (i) => setFaqOpen((prev) => ({ ...prev, [i]: !prev[i] }));

  return (
    <div className="automatic-time-tracking-container">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={CANONICAL} />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      <div className="relative min-h-screen overflow-hidden bg-white">
        <PublicHeader />
        <div className="pt-16 sm:pt-18 md:pt-20">
          {/* Background Effects */}
          <div
            className="absolute inset-0"
            style={{
              background: "radial-gradient(ellipse at top, #FFFFFF, #FFFFFF)",
            }}
          ></div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            {/* Hero: full-bleed gradient; content aligned with header */}
            <div
              className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen pt-16 sm:pt-18 md:pt-20 pb-10 sm:pb-12 md:pb-14 px-4 sm:px-6 lg:px-8"
              style={{
                background:
                  "radial-gradient(141.42% 141.42% at 0% 0%, rgba(230, 242, 255, 0.5) 0%, rgba(233, 252, 250, 0) 50%), radial-gradient(141.42% 141.42% at 100% 100%, rgba(240, 247, 255, 0.5) 0%, rgba(240, 247, 255, 0) 50%), #FFFFFF",
              }}
            >
              <div className="max-w-7xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="mb-0 text-center"
                >
                  <div className="flex items-center justify-center mb-6">
                    <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#00D1C1]/10 border border-[#00D1C1]/20">
                      <Clock className="w-4 h-4 text-[#00D1C1]" />
                      <span className="text-sm font-semibold text-[#00D1C1]">
                        TIME INTELLIGENCE
                      </span>
                    </div>
                  </div>
                  <h1
                    className="font-extrabold tracking-normal text-gray-900 mb-4 text-center"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "clamp(2.5rem, 5vw, 62px)",
                      lineHeight: "clamp(50px, 8vw, 72px)",
                    }}
                  >
                    <span className="text-gray-900">Automatic </span>
                    <span className="text-[#00D1C1]">Time Tracking</span>
                  </h1>
                  <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto leading-relaxed px-4 sm:px-0">
                    Silent, precise tracking across all apps and projects. Zero
                    manual input required, just install and let TeamGrid do the
                    rest.
                  </p>
                </motion.div>
              </div>
            </div>

            {/* Main Content */}
            <section className="py-8 sm:py-12 lg:py-16 bg-white">
              <div className="w-full">
                <div className="bg-white rounded-2xl py-6 sm:py-8 lg:py-10 px-4 sm:px-6 md:px-8 lg:px-10 mb-4">
                  <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                    {/* Left Section - Feature Details (matches design) */}
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.3 }}
                      className="flex flex-col justify-center"
                    >
                      <h1 className="text-3xl md:text-4xl lg:text-[2.5rem] font-bold text-black leading-tight">
                        Track Every Minute Automatically
                      </h1>
                      <p className="mt-4 text-base text-gray-600 max-w-xl leading-relaxed">
                        Our lightweight desktop agent runs silently in the
                        background. Capturing every application, website, and
                        project context without any manual timers or input.
                      </p>

                      {/* Key Features - teal circle outline icons */}
                      <div className="space-y-6 mt-8 mb-10">
                        <div className="flex items-start gap-4">
                          <div className="w-11 h-11 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center">
                            <img
                              src="/Background+Border+Shadow.webp"
                              alt="Desktop Agent"
                              className="w-11 h-11 object-contain"
                            />
                          </div>
                          <div>
                            <h3 className="font-bold text-black">
                              Desktop Agent
                            </h3>
                            <p className="text-sm text-gray-600 mt-0.5 leading-relaxed">
                              Lightweight app runs silently with minimal CPU
                              usage.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-4">
                          <div className="w-11 h-11 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center">
                            <img
                              src="/Background+Border+Shadow_3.webp"
                              alt="Privacy First"
                              className="w-11 h-11 object-contain"
                            />
                          </div>
                          <div>
                            <h3 className="font-bold text-black">
                              Privacy First
                            </h3>
                            <p className="text-sm text-gray-600 mt-0.5 leading-relaxed">
                              Activity data stays secure with end-to-end
                              encryption.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-4">
                          <div className="w-11 h-11 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center">
                            <img
                              src="/Background+Border+Shadow_2.webp"
                              alt="Real-Time Sync"
                              className="w-11 h-11 object-contain"
                            />
                          </div>
                          <div>
                            <h3 className="font-bold text-black">
                              Real-Time Sync
                            </h3>
                            <p className="text-sm text-gray-600 mt-0.5 leading-relaxed">
                              Data syncs instantly to your dashboard.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* CTA: Download Agent (teal) + See How It Works (bordered) */}
                      <div className="flex flex-wrap items-center gap-3">
                        <motion.button
                          type="button"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => navigate("/download")}
                          className="px-6 py-3 rounded-lg font-bold text-black bg-[#00D1C1] shadow-[0_2px_8px_rgba(0,209,193,0.35)] hover:bg-[#00B8AA] transition-colors"
                        >
                          Download Agent
                        </motion.button>
                        <motion.button
                          type="button"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => navigate("/book-demo")}
                          className="px-6 py-3 rounded-lg font-semibold text-[#6B7280] bg-white border border-[#D1D5DB] shadow-sm hover:border-gray-400 transition-colors"
                        >
                          See How It Works
                        </motion.button>
                      </div>
                    </motion.div>

                    {/* Right Section - Activity Tracking card (matches design) */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                      className="w-full max-w-[520px] lg:ml-auto bg-white rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.08)] border border-gray-100"
                    >
                      {/* Header - large bold title, smaller subtitle */}
                      <div className="px-6 pt-6 pb-4">
                        <h3 className="text-xl font-bold text-gray-800">
                          Activity Tracking
                        </h3>
                        <p className="text-sm font-normal text-gray-600 mt-0.5">
                          Today 9am-6pm
                        </p>

                        {/* Summary cards - grey bg, icon + label on same row */}
                        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="w-[110px] h-[110px] bg-gray-100 rounded-lg p-3 flex flex-col shrink-0">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0">
                                <Clock className="w-4 h-4 text-[#00D1C1]" />
                              </div>
                              <span className="text-xs font-normal text-gray-700">
                                Active
                              </span>
                            </div>
                            <div className="text-lg font-bold text-gray-800 mt-2">
                              6h
                            </div>
                            <div className="text-xs font-normal text-gray-600">
                              27m
                            </div>
                          </div>
                          <div className="w-[110px] h-[110px] bg-gray-100 rounded-lg p-3 flex flex-col shrink-0">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0">
                                <Coffee className="w-4 h-4 text-amber-700" />
                              </div>
                              <span className="text-xs font-normal text-gray-700">
                                Idle
                              </span>
                            </div>
                            <div className="text-lg font-bold text-gray-800 mt-2">
                              0
                            </div>
                          </div>
                          <div className="w-[110px] h-[110px] bg-gray-100 rounded-lg p-3 flex flex-col shrink-0">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0">
                                <Code2 className="w-4 h-4 text-indigo-600" />
                              </div>
                              <span className="text-xs font-normal text-gray-700">
                                Dev
                              </span>
                            </div>
                            <div className="text-lg font-bold text-gray-800 mt-2">
                              6h
                            </div>
                            <div className="text-xs font-normal text-gray-600">
                              27m
                            </div>
                          </div>
                          <div className="w-[110px] h-[110px] bg-gray-100 rounded-lg p-3 flex flex-col shrink-0">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0">
                                <Timer className="w-4 h-4 text-amber-500" />
                              </div>
                              <span className="text-xs font-normal text-gray-700">
                                Prod
                              </span>
                            </div>
                            <div className="text-lg font-bold text-gray-800 mt-2">
                              1h
                            </div>
                            <div className="text-xs font-normal text-gray-600">
                              42m
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* App usage - white rows, icon + name + progress bar + time */}
                      <div className="px-6 pb-6 space-y-3">
                        <div className="bg-white rounded-lg border border-gray-100 p-4 flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                            <Code2 className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-normal text-gray-700">
                                VS Code
                              </span>
                              <span className="text-sm font-medium text-[#00D1C1] shrink-0">
                                4h 53m
                              </span>
                            </div>
                            <div className="mt-2 h-2 rounded-full bg-gray-200 overflow-hidden">
                              <div
                                className="h-full bg-[#00D1C1] rounded-full"
                                style={{ width: "78%" }}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg border border-gray-100 p-4 flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
                            <Globe className="w-5 h-5 text-orange-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-normal text-gray-700">
                                Google Chrome
                              </span>
                              <span className="text-sm font-normal text-gray-700 shrink-0">
                                1h 22m
                              </span>
                            </div>
                            <div className="mt-2 h-2 rounded-full bg-gray-200 overflow-hidden">
                              <div
                                className="h-full bg-orange-500 rounded-full"
                                style={{ width: "32%" }}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg border border-gray-100 p-4 flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                            <Calendar className="w-5 h-5 text-purple-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-normal text-gray-700">
                                Microsoft Teams
                              </span>
                              <span className="text-sm font-normal text-gray-700 shrink-0">
                                12m
                              </span>
                            </div>
                            <div className="mt-2 h-2 rounded-full bg-gray-200 overflow-hidden">
                              <div
                                className="h-full bg-purple-500 rounded-full"
                                style={{ width: "12%" }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </section>

            {/* Testimonials Section */}
            <Testimonials />

            {/* FAQ Section - full-bleed gray bg */}
            <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen py-16 bg-[#F3F4F6]">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center mb-12"
                >
                  <span className="inline-block px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wide text-[#0D9488] bg-[#CCFBF1] border border-[#99F6E4] mb-6">
                    GOT QUESTIONS?
                  </span>
                  <h2 className="text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] font-bold text-black mb-4">
                    Frequently Asked Questions
                  </h2>
                  <p className="text-lg text-gray-600">
                    Everything you need to know about TeamGrid for automatic
                    time tracking
                  </p>
                </motion.div>

                <div className="space-y-4 w-full max-w-[720px] mx-auto">
                  {[
                    {
                      question:
                        "How accurate is the Automatic Time Tracking feature?",
                      answer:
                        "TeamGrid's automatic tracking precisely records active app/website usage, focus periods, and idle time to give a true picture of where work time is spent, helping teams understand their real productivity.",
                    },
                  ].map((faq, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.1 * index }}
                      className="w-full rounded-xl p-6 cursor-pointer bg-white"
                      style={{
                        border: faqOpen[index]
                          ? "1px solid #28B4AE"
                          : "1px solid #D1D5DB",
                        transition: "border-color 0.15s ease-in-out",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h3
                          className="text-left font-bold text-[#1A1A1A] flex-1"
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 16,
                            lineHeight: "26px",
                          }}
                        >
                          {faq.question}
                        </h3>
                        <span className="flex-shrink-0">
                          {faqOpen[index] ? (
                            <Minus className="w-5 h-5 text-[#111111]" />
                          ) : (
                            <Plus className="w-5 h-5 text-[#111111]" />
                          )}
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {faqOpen[index] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <p
                              className="mt-4"
                              style={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 400,
                                fontSize: 16,
                                lineHeight: "26px",
                                color: "#374151",
                              }}
                            >
                              {faq.answer}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            <ReadyToTransform />

            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomaticTimeTracking;
